import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Container, RefreshCcw, ShieldAlert, Wrench } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataErrorBanner, type DataErrorDetail } from '@/components/ui/data-error-banner';
import {
  formatMoney,
  type DepotBillableCharge,
  type DepotContainerVisit,
  type DepotRepairEstimate,
  useDepotList,
} from './depot-hooks';
import {
  DepotEmptyRow,
  DepotGridPanel,
  depotTableClassName,
  depotTableHeadClassName,
  depotTdClassName,
  depotThClassName,
} from './depot-grid';

type AccountRollup = {
  accountName: string;
  inYard: number;
  completed: number;
  onHold: number;
  repairCases: number;
  unbilledAmount: number;
  disputedAmount: number;
};

function normalizeKey(value?: string | null): string {
  return value?.trim() || 'Unassigned';
}

function toAmount(value: string | number | null | undefined): number {
  const parsed = typeof value === 'string' ? Number(value) : value ?? 0;
  return Number.isFinite(parsed) ? Number(parsed) : 0;
}

export default function DepotAccountsPage() {
  const containers = useDepotList<DepotContainerVisit>('/depot/containers?limit=100');
  const charges = useDepotList<DepotBillableCharge>('/depot/charges/billable?limit=100');
  const repairs = useDepotList<DepotRepairEstimate>('/depot/repair-estimates?limit=100');
  const [refreshing, setRefreshing] = useState(false);

  const error = containers.error ?? charges.error ?? repairs.error;
  const loading = containers.loading || charges.loading || repairs.loading;

  const pageError: DataErrorDetail | null = error
    ? {
        endpoint: '/depot/containers + /depot/charges/billable + /depot/repair-estimates',
        message: error,
        status: 500,
        code: 'DEPOT_ACCOUNTS_LOAD_FAILED',
      }
    : null;

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([containers.refresh(), charges.refresh(), repairs.refresh()]);
    setRefreshing(false);
  };

  const accountRows = useMemo(() => {
    const map = new Map<string, AccountRollup>();

    const ensure = (name?: string | null) => {
      const key = normalizeKey(name);
      const current = map.get(key);
      if (current) return current;
      const next: AccountRollup = {
        accountName: key,
        inYard: 0,
        completed: 0,
        onHold: 0,
        repairCases: 0,
        unbilledAmount: 0,
        disputedAmount: 0,
      };
      map.set(key, next);
      return next;
    };

    containers.items.forEach((item) => {
      const row = ensure(item.line_operator);
      if (item.visit_status === 'completed') row.completed += 1;
      else row.inYard += 1;
      if (item.hold_status && item.hold_status !== 'clear') row.onHold += 1;
    });

    charges.items.forEach((item) => {
      const row = ensure(item.line_operator ?? item.bill_to_name);
      row.unbilledAmount += toAmount(item.amount);
      if (item.charge_status === 'disputed') row.disputedAmount += toAmount(item.amount);
    });

    repairs.items.forEach((item) => {
      const visit = containers.items.find((container) => container.id === item.visit_id);
      const row = ensure(visit?.line_operator);
      row.repairCases += 1;
    });

    return Array.from(map.values()).sort((a, b) => (b.unbilledAmount - a.unbilledAmount) || (b.inYard - a.inYard));
  }, [charges.items, containers.items, repairs.items]);

  const totals = useMemo(() => ({
    activeAccounts: accountRows.length,
    inYard: containers.items.filter((item) => item.visit_status !== 'completed').length,
    holdAccounts: accountRows.filter((item) => item.onHold > 0).length,
    unbilledAmount: charges.items.reduce((sum, item) => sum + toAmount(item.amount), 0),
  }), [accountRows, charges.items, containers.items]);

  return (
    <div className="space-y-5">
      {pageError && <DataErrorBanner error={pageError} entityLabel="Depot Accounts" onRetry={handleRefresh} />}

      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Depot Accounts</h2>
          <p className="text-sm text-muted-foreground">
            Line operator and bill-to account view for yard stock, holds, repairs, and unbilled services.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/revenue">Revenue View</Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/depot/billing">Billing Desk</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || loading}>
            <RefreshCcw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Active Accounts</span>
            <Building2 className="h-4 w-4 text-sky-700" />
          </div>
          <div className="text-2xl font-bold">{loading ? '—' : totals.activeAccounts}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>In-Yard Units</span>
            <Container className="h-4 w-4 text-emerald-700" />
          </div>
          <div className="text-2xl font-bold">{loading ? '—' : totals.inYard}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Accounts on Hold</span>
            <ShieldAlert className="h-4 w-4 text-rose-700" />
          </div>
          <div className="text-2xl font-bold">{loading ? '—' : totals.holdAccounts}</div>
        </div>
        <div className="rounded-lg border bg-background px-3 py-3">
          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
            <span>Unbilled Services</span>
            <Wrench className="h-4 w-4 text-violet-700" />
          </div>
          <div className="text-2xl font-bold">{loading ? '—' : formatMoney(totals.unbilledAmount)}</div>
        </div>
      </div>

      <DepotGridPanel title="Operator Portfolio" subtitle="Customer/account position by yard stock, hold, repair, and unbilled exposure.">
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Account</th>
              <th className={depotThClassName}>In Yard</th>
              <th className={depotThClassName}>Completed</th>
              <th className={depotThClassName}>Holds</th>
              <th className={depotThClassName}>Repairs</th>
              <th className={depotThClassName}>Unbilled</th>
              <th className={depotThClassName}>Disputed</th>
              <th className={depotThClassName}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {accountRows.length === 0 ? (
              <DepotEmptyRow colSpan={8}>No depot account activity yet.</DepotEmptyRow>
            ) : (
              accountRows.map((item) => (
                <tr key={item.accountName} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-medium`}>{item.accountName}</td>
                  <td className={depotTdClassName}>{item.inYard}</td>
                  <td className={depotTdClassName}>{item.completed}</td>
                  <td className={depotTdClassName}>
                    <Badge variant={item.onHold > 0 ? 'destructive' : 'secondary'}>
                      {item.onHold > 0 ? `${item.onHold} hold` : 'clear'}
                    </Badge>
                  </td>
                  <td className={depotTdClassName}>{item.repairCases}</td>
                  <td className={depotTdClassName}>{formatMoney(item.unbilledAmount)}</td>
                  <td className={depotTdClassName}>{formatMoney(item.disputedAmount)}</td>
                  <td className={depotTdClassName}>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="ghost" asChild>
                        <Link to={`/depot/revenue?account=${encodeURIComponent(item.accountName)}`}>Revenue</Link>
                      </Button>
                      <Button size="sm" variant="ghost" asChild>
                        <Link to={`/depot/profitability?account=${encodeURIComponent(item.accountName)}`}>Profitability</Link>
                      </Button>
                      <Button size="sm" variant="ghost" asChild>
                        <Link to={`/depot/tariff?account=${encodeURIComponent(item.accountName)}`}>Tariff</Link>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>

      <DepotGridPanel
        title="Repair Approval Pressure"
        subtitle="Repair estimate pipeline that may block release or billing."
        action={<Button variant="ghost" size="sm" asChild><Link to="/depot/maintenance">Open Maintenance</Link></Button>}
      >
        <table className={depotTableClassName}>
          <thead className={depotTableHeadClassName}>
            <tr>
              <th className={depotThClassName}>Estimate</th>
              <th className={depotThClassName}>Container</th>
              <th className={depotThClassName}>Depot</th>
              <th className={depotThClassName}>Amount</th>
              <th className={depotThClassName}>Estimate Status</th>
              <th className={depotThClassName}>Approval</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {repairs.items.length === 0 ? (
              <DepotEmptyRow colSpan={6}>No repair estimate pipeline.</DepotEmptyRow>
            ) : (
              repairs.items.slice(0, 8).map((item) => (
                <tr key={item.id} className="hover:bg-muted/30">
                  <td className={`${depotTdClassName} font-mono font-semibold`}>{item.estimate_no}</td>
                  <td className={depotTdClassName}>{item.container_no}</td>
                  <td className={depotTdClassName}>{item.depot_code ?? 'Depot TBC'}</td>
                  <td className={depotTdClassName}>{formatMoney(item.total_amount, item.currency)}</td>
                  <td className={depotTdClassName}>{item.estimate_status}</td>
                  <td className={depotTdClassName}>
                    <Badge variant={item.approval_status === 'pending' ? 'destructive' : 'outline'}>
                      {item.approval_status}
                    </Badge>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DepotGridPanel>
    </div>
  );
}
